import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TableHeader, ProgressBar, Spinner, WithRoleAccess } from "@services/ui-components";
import { getSteps, IStep } from "@me-team/common/src/components/Me-menu/MenuItemsPage/constants";
import QrMenuPreview from "./components/QrMenuPreview";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useLinkAndQrCodeMenu } from "./hooks/useLinkAndQrCodeMenu";
import ErrorService from "../../../services/ErrorService";
import { ApolloError, useReactiveVar } from "@apollo/client";
import {
    canEditMenuContentByStatus,
    getMenuTypeLabel,
    isMenuActivated,
    toMenuTypeEnum,
} from "../AllMenusPage/menuEnumMappings";
import { CompanyAccess, MenuTypeType } from "@me-team/host/main/graphql/types";
import Button from "react-bootstrap/Button";
import { useActiveMenusByType } from "../AllMenusPage/hooks/useActiveMenusByType";
import { useMenuStatusActions } from "../AllMenusPage/hooks/useMenuStatusActions";
import ConfirmReplaceMenuModal from "../AllMenusPage/modals/ConfirmReplaceMenuModal/ConfirmReplaceMenuModal";
import ConfirmPublishMenuModal from "../AllMenusPage/modals/ConfirmPublishMenuModal/ConfirmPublishMenuModal";
import { cloneNameToDisplay } from "../utils/cloneNameUtils";
import { useGetOneMenuQuery } from "@me-team/host/main/menu/graphql/menu.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const MenuViewPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId, id } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const steps: IStep[] = getSteps(t);
    const canCreateMenu = useRoleAccess(CompanyAccess.MenuCreate);
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { data: dataOneMenu, loading: dataOneMenuLoading } = useGetOneMenuQuery({
        skip: !currentCompanyId,
        fetchPolicy: "network-only",
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            menuId: Number(id),
        },
        onError: (apolloError: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
        },
    });

    const menuOneData = dataOneMenu?.user?.company?.branches?.[0]?.menusList?.[0];
    const isActivated = isMenuActivated(menuOneData?.status?.slug);
    const canOpenMenuContent = canEditMenuContentByStatus(menuOneData?.status?.slug, canCreateMenu, canCloneEditMenu);

    const { menu, qrMenuQrCodeLink, qrMenuLink, linkAndQrCodeMenuLoading, linkAndQrCodeMenuRefetch } =
        useLinkAndQrCodeMenu({
            branchId,
            menuId: !isActivated ? id : null,
            menusListId: id,
            onError: (apolloError: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
            },
        });

    const { publishMenu, replaceMenu, menuAction, setMenuAction, loading } = useMenuStatusActions({
        branchId: Number(branchId),
        t,
        onSuccess: async () => {
            await linkAndQrCodeMenuRefetch();
            setMenuAction(null);
        },
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { activeMenusType } = useActiveMenusByType();

    const menuType = toMenuTypeEnum(menu?.type?.slug);
    const menuTypeLabel = getMenuTypeLabel(menuType, t);

    const activeMenuMap = {
        [MenuTypeType.DineIn]: activeMenusType.DINE_IN,
        [MenuTypeType.Delivery]: activeMenusType.DELIVERY,
        [MenuTypeType.Pickup]: activeMenusType.PICK_UP,
    };

    const currentActiveMenu = menuType ? activeMenuMap[menuType] : null;

    const handlePublishClick = () => {
        if (!canCloneEditMenu) return;
        if (!menu) return;
        setMenuAction({
            type: currentActiveMenu ? "replace" : "publish",
            menu,
        });
    };

    if (linkAndQrCodeMenuLoading || dataOneMenuLoading) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <TableHeader
                title={t("Menu preview")}
                paths={[
                    { path: `/branches/${branchId}`, label: t("Branches") },
                    { path: `/menu/all-menus/branch/${branchId}`, label: t("All menu") },
                    { path: canOpenMenuContent ? `/menu/page/${id}/branch/${branchId}` : "", label: menuTypeLabel },
                    { path: "", label: t("Menu preview") },
                ]}
                colSize={7}
                btnColSize={5}
            ></TableHeader>
            <ProgressBar currentStep={3} steps={steps} marginBottom="mb-0" isMenu />
            <div className="py-5 px-4 mt-4 bg-violet rounded-20">
                <h3 className="fs-4 text-center">{t("Check and test your menu!")}</h3>
                {isActivated ? (
                    <Col sm={10} md={8} xl={6} className="mx-auto pt-3">
                        <div className="d-flex align-items-center px-6 py-2 border border-orange text-orange bg-additional-orange rounded-1">
                            <i className="bi bi-info-circle me-2"></i>
                            <p className="fs-7 mb-0">
                                {t("The QR code and link are intended for testing this menu only.")}
                            </p>
                        </div>
                    </Col>
                ) : null}
                <QrMenuPreview qrMenuQrCodeLink={qrMenuQrCodeLink} qrMenuLink={qrMenuLink} />
                <Col lg={7} xl={6} xxl={5} className="mx-lg-auto">
                    <Row className="justify-content-xxl-center">
                        {canOpenMenuContent ? (
                            <Col xs={6} xxl={5}>
                                <Link
                                    to={`/menu/page/${id}/branch/${branchId}`}
                                    className="btn btn-outline-primary w-100 fs-7 px-1"
                                    onClick={(event) => {
                                        if (!canOpenMenuContent) event.preventDefault();
                                    }}
                                >
                                    <i className="bi bi-chevron-left me-2"></i>
                                    {t("To edit")}
                                </Link>
                            </Col>
                        ) : null}
                        {!isActivated ? (
                            <WithRoleAccess permission={CompanyAccess.MenuCloneEdit}>
                                <Col xs={6} xxl={5}>
                                    <Button
                                        variant="primary"
                                        className="w-100 fs-7 fw-bold text-capitalize px-1"
                                        onClick={handlePublishClick}
                                    >
                                        {t("To publish")}
                                    </Button>
                                </Col>
                            </WithRoleAccess>
                        ) : null}
                    </Row>
                </Col>
            </div>

            {menuAction?.type === "publish" && (
                <ConfirmPublishMenuModal
                    show={true}
                    onClose={() => setMenuAction(null)}
                    onConfirm={async () => {
                        if (!canCloneEditMenu || !menuAction.menu || isSubmitting) return;
                        try {
                            setIsSubmitting(true);
                            await publishMenu(menuAction.menu);
                            navigate(`/menu/${menuAction.menu.id}/before-publish/branch/${branchId}`);
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                    menuTypeName={t(menuAction.menu?.type?.name ?? "")}
                    loading={loading || isSubmitting}
                />
            )}

            {menuAction?.type === "replace" && (
                <ConfirmReplaceMenuModal
                    show={true}
                    onClose={() => setMenuAction(null)}
                    onConfirm={async () => {
                        if (!canCloneEditMenu || !menuAction.menu || isSubmitting) return;
                        try {
                            setIsSubmitting(true);
                            await replaceMenu(menuAction.menu);
                            navigate(`/menu/${menuAction.menu.id}/before-publish/branch/${branchId}`);
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                    menuTypeName={t(menuAction.menu?.type?.name)}
                    menuName={cloneNameToDisplay(currentActiveMenu?.name, t)}
                    loading={loading || isSubmitting}
                />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default MenuViewPage;
